import { useCallback, useEffect, useState } from 'react';
import { Feather, AlertCircle, Gift, Mail } from 'lucide-react';
import { useSignInMutation } from '../auth/api';
import { useInviteInfoMutation, useOnboardMutation } from './api';

/**
 * The invitee's landing page (the mail gateway's acquisition step).
 *
 * Reached via `/?invite=<token>&email=<address>`. It tells them who wrote, lets
 * them keep or change the provisional handle, sets a password, and signs them
 * straight in — where the full reply is already waiting.
 *
 * Laid out with the same `auth-*` / `field` classes as `AuthScreen`; the invite
 * is the same kind of moment, so it should look like it.
 */
export function InviteScreen({
  token,
  email,
  onJoined,
}: {
  token: string;
  email: string;
  /** The pair with whoever invited them, to open once the first-run tour is done. */
  onJoined?: (relationshipId: string) => void;
}) {
  const [getInfo] = useInviteInfoMutation();
  const [onboard, { isLoading }] = useOnboardMutation();
  const [signIn] = useSignInMutation();
  const [inviter, setInviter] = useState<string | null>(null);
  const [gift, setGift] = useState<{ collectionName: string } | null>(null);
  const [handle, setHandle] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void getInfo({ token, email })
      .unwrap()
      .then((info) => {
        setInviter(info.inviterHandle);
        setGift(info.gift ?? null);
        setHandle(info.provisionalHandle);
      })
      .catch(() => setError("This invite doesn't look right — check the link and try again."))
      .finally(() => setLoading(false));
  }, [token, email, getInfo]);

  const onSubmit = useCallback(async () => {
    setError(null);
    try {
      const { relationshipId } = await onboard({ token, email, password, handle }).unwrap();
      await signIn({ email, password }).unwrap();
      window.history.replaceState({}, '', window.location.pathname);
      // Like an invite link: they land on a blank page addressed to whoever invited them.
      if (relationshipId) onJoined?.(relationshipId);
    } catch (e) {
      const err = e as { data?: { message?: string | string[] }; status?: unknown };
      if (Array.isArray(err.data?.message)) setError(err.data!.message[0]!);
      else setError(err.data?.message ?? (err.status === 'FETCH_ERROR' ? 'offline' : 'could not claim your account'));
    }
  }, [onboard, signIn, token, email, password, handle, onJoined]);

  const canSubmit = !isLoading && handle.trim().length >= 3 && password.length >= 8;

  return (
    <main className="auth-page">
      <div className="auth-brand">
        <div className="auth-logo">
          <Feather size={26} />
        </div>
        <h1 className="auth-title">toiyp</h1>
        <p className="auth-tagline">letters, slowly</p>
      </div>

      <div className="auth-card">
        {gift ? (
          <div className="invite-note">
            <Gift size={16} aria-hidden="true" className="invite-note__icon" />
            <div>
              <h2 className="invite-note__title">
                {inviter ? <>@{inviter} has sent you a stamp book</> : 'Someone has sent you a stamp book'}
              </h2>
              <p className="invite-note__gift">
                <Gift size={14} aria-hidden="true" /> {gift.collectionName}, with a note from them
              </p>
              <p className="invite-note__body">
                It&apos;s waiting here for you. Claim your account and it will be in your stamp book, with
                a blank page addressed to {inviter ? <strong>@{inviter}</strong> : 'them'}. Nothing is sent
                until you write it.
              </p>
            </div>
          </div>
        ) : (
        <div className="invite-note">
          <Mail size={16} aria-hidden="true" className="invite-note__icon" />
          <div>
            <h2 className="invite-note__title">You have a letter waiting</h2>
            <p className="invite-note__body">
              {loading ? (
                'Checking your post…'
              ) : inviter ? (
                <>
                  <strong>@{inviter}</strong> wrote to you. The first lines are in the email — the
                  whole letter is here, waiting for you.
                </>
              ) : (
                'Someone wrote to you. The first lines are in the email — the whole letter is waiting.'
              )}
            </p>
          </div>
        </div>
        )}

        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
        >
          <div className="field">
            <label className="field__label" htmlFor="invite-email">Email</label>
            <input id="invite-email" className="field__input" value={email} readOnly disabled />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="invite-handle">Your @handle</label>
            <input
              id="invite-handle"
              className="field__input"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="your handle"
              spellCheck={false}
              autoComplete="off"
              aria-describedby="invite-handle-hint"
            />
            <p className="field__hint" id="invite-handle-hint">
              This is the one we made for you from your email address. You can keep it or choose
              another — your letters stay where they are either way.
            </p>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="invite-password">Password</label>
            <input
              id="invite-password"
              className="field__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="at least 8 characters"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          {error && (
            <p className="form-error" role="alert">
              <AlertCircle size={14} aria-hidden="true" />
              {error}
            </p>
          )}

          <button type="submit" className="form-submit" disabled={!canSubmit}>
            {isLoading ? 'Claiming…' : 'Claim your account'}
          </button>
        </form>
      </div>
    </main>
  );
}